/**
 * Operações imutáveis sobre WorkflowGraph.
 * Manipulam nodes/edges/snippets sem mutação direta.
 */

import { nanoid } from 'nanoid';
import type {
  EdgeStyle,
  NodeKind,
  NodeConfigByKind,
  WorkflowEdge,
  WorkflowGraph,
  WorkflowNode,
} from '../types';

export const newId = (prefix = 'n') => `${prefix}_${nanoid(8)}`;

const defaultConfigs: { [K in NodeKind]: NodeConfigByKind[K] } = {
  trigger: { event: 'ORDER_CREATED' },
  switch: { cases: [], defaultNextNodeId: null },
  disp: { title: 'Novo envio', action: 'CHEAPEST_METHOD', timing: 'IMMEDIATE' },
  'smart-disp': {
    priceSpeedTradeoff: 3,
    allowedMethodIds: [],
    maxRetries: 2,
    maxAttemptMinutes: 240,
  },
  check: { waitMinutes: 30, carrier: null, stuckInStatuses: [] },
  event: { onStatuses: [] },
  delay: { minutes: 120 },
  cancel: { reason: 'Cancelamento automático', notifyCarrier: true },
  'apply-tag': { tagId: null },
  'update-fields': {
    updates: [{ id: nanoid(6), fieldPath: '', value: '' }],
  },
  'create-dispute': { initialSubStatus: 'with_brand', reason: '' },
  notify: {
    channels: ['email'],
    recipient: 'customer',
    messagesByChannel: { email: { body: '' } },
  },
  'http-request': {
    method: 'POST',
    url: '',
    headers: [{ key: 'Content-Type', value: 'application/json' }],
    body: '',
  },
};

export function makeNode<K extends NodeKind>(
  kind: K,
  position: { x: number; y: number } = { x: 0, y: 0 },
  config?: Partial<NodeConfigByKind[K]>,
  snippetId?: string,
): WorkflowNode<K> {
  const def = defaultConfigs[kind];
  return {
    id: newId(kind),
    kind,
    position,
    config: { ...(def as object), ...(config as object) } as NodeConfigByKind[K],
    snippetId,
  };
}

export function addNode(graph: WorkflowGraph, node: WorkflowNode): WorkflowGraph {
  return { ...graph, nodes: [...graph.nodes, node] };
}

export function updateNode(
  graph: WorkflowGraph,
  nodeId: string,
  patch: Partial<WorkflowNode>,
): WorkflowGraph {
  return {
    ...graph,
    nodes: graph.nodes.map((n) => (n.id === nodeId ? { ...n, ...patch } : n)),
  };
}

export function updateNodeConfig<K extends NodeKind>(
  graph: WorkflowGraph,
  nodeId: string,
  configPatch: Partial<NodeConfigByKind[K]>,
): WorkflowGraph {
  return {
    ...graph,
    nodes: graph.nodes.map((n) =>
      n.id === nodeId
        ? { ...n, config: { ...(n.config as object), ...(configPatch as object) } as NodeConfigByKind[NodeKind] }
        : n,
    ),
  };
}

export function removeNode(graph: WorkflowGraph, nodeId: string): WorkflowGraph {
  return {
    ...graph,
    nodes: graph.nodes.filter((n) => n.id !== nodeId),
    edges: graph.edges.filter((e) => e.fromNodeId !== nodeId && e.toNodeId !== nodeId),
    snippets: graph.snippets
      .map((s) => ({ ...s, nodeIds: s.nodeIds.filter((id) => id !== nodeId) }))
      .filter((s) => s.nodeIds.length > 0),
  };
}

export function addEdge(
  graph: WorkflowGraph,
  fromNodeId: string,
  fromPortId: string,
  toNodeId: string,
  style: EdgeStyle = 'default',
): WorkflowGraph {
  // dedup: se já existe edge da mesma porta, substitui
  const existing = graph.edges.findIndex(
    (e) => e.fromNodeId === fromNodeId && e.fromPortId === fromPortId,
  );
  const next: WorkflowEdge = {
    id: newId('edge'),
    fromNodeId,
    fromPortId,
    toNodeId,
    style,
  };
  const edges = [...graph.edges];
  if (existing >= 0) edges[existing] = next;
  else edges.push(next);
  return { ...graph, edges };
}

export function removeEdge(graph: WorkflowGraph, edgeId: string): WorkflowGraph {
  return { ...graph, edges: graph.edges.filter((e) => e.id !== edgeId) };
}
